import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { getErrorMessage } from '../api/errors';
import AddressSelectList from '../components/AddressSelectList';
import ConsentCheckbox, { LegalLink } from '../components/ConsentCheckbox';
import CouponInput from '../components/CouponInput';
import IyzicoCheckoutForm from '../components/IyzicoCheckoutForm';
import PreInformationSummary from '../components/PreInformationSummary';
import { env } from '../config/env';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useCoupon } from '../context/CouponContext';
import { addressesService } from '../services';
import { paymentService } from '../services/payments/paymentService';
import {
  buildIyzicoInitiateRequest,
  buildIyzicoOrderRequest,
  calculateOrderTotals,
  type CheckoutAddressState,
} from '../utils/checkout';
import { getDefaultBillingAddress, getDefaultShippingAddress } from '../utils/address';
import { savePendingIyzicoCheckout } from '../utils/iyzicoPending';
import { formatAmount, STORE_CURRENCY } from '../utils/formatPrice';
import { getPrimaryImage, getUnitPrice } from '../utils/product';
import type { UserAddress } from '../types';

type CheckoutStep = 'details' | 'payment';
type AddressSelection = 'new' | string;

function isMockPaymentUrl(url: string): boolean {
  try {
    return new URL(url).searchParams.has('mock');
  } catch {
    return url.includes('mock=1');
  }
}

export default function Checkout() {
  const { items, totalPrice } = useCart();
  const { user } = useAuth();
  const { appliedCoupon, applyCoupon, removeCoupon } = useCoupon();
  const navigate = useNavigate();

  const [step, setStep] = useState<CheckoutStep>('details');
  const [loading, setLoading] = useState(false);
  const [completingMock, setCompletingMock] = useState(false);
  const [error, setError] = useState('');
  const [preInformationAccepted, setPreInformationAccepted] = useState(false);
  const [distanceSalesAccepted, setDistanceSalesAccepted] = useState(false);
  const [returnPolicyAccepted, setReturnPolicyAccepted] = useState(false);
  const [paymentToken, setPaymentToken] = useState<string | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [checkoutFormContent, setCheckoutFormContent] = useState<string | null>(null);
  const [isBackendMockPayment, setIsBackendMockPayment] = useState(false);

  const [savedAddresses, setSavedAddresses] = useState<UserAddress[]>([]);
  const [addressesLoading, setAddressesLoading] = useState(true);
  const [shippingSelection, setShippingSelection] = useState<AddressSelection>('new');
  const [billingSameAsShipping, setBillingSameAsShipping] = useState(true);
  const [billingSelection, setBillingSelection] = useState<AddressSelection>('new');
  const [saveShippingAddress, setSaveShippingAddress] = useState(false);
  const [saveBillingAddress, setSaveBillingAddress] = useState(false);

  const totals = calculateOrderTotals(totalPrice, appliedCoupon);

  const nameParts = user?.name.split(' ') ?? ['', ''];
  const firstName = nameParts[0] ?? '';
  const lastName = nameParts.slice(1).join(' ') ?? '';

  const defaultShipping = useMemo(
    () => getDefaultShippingAddress(savedAddresses),
    [savedAddresses],
  );

  useEffect(() => {
    if (!user) return;

    void (async () => {
      setAddressesLoading(true);
      try {
        const addresses = await addressesService.fetchAddresses();
        setSavedAddresses(addresses);
        const shippingDefault = getDefaultShippingAddress(addresses);
        const billingDefault = getDefaultBillingAddress(addresses);
        if (shippingDefault) {
          setShippingSelection(shippingDefault.id);
        }
        if (billingDefault) {
          setBillingSelection(billingDefault.id);
        }
      } catch {
        setSavedAddresses([]);
      } finally {
        setAddressesLoading(false);
      }
    })();
  }, [user]);

  useEffect(() => {
    if (!appliedCoupon) return;

    void applyCoupon(appliedCoupon.code, totalPrice).catch(() => {
      removeCoupon();
    });
  }, [totalPrice]); // eslint-disable-line react-hooks/exhaustive-deps

  const addressState: CheckoutAddressState = {
    shippingMode: shippingSelection === 'new' ? 'new' : 'saved',
    shippingAddressId: shippingSelection === 'new' ? null : shippingSelection,
    saveShippingAddress: shippingSelection === 'new' ? saveShippingAddress : false,
    billingSameAsShipping,
    billingMode: billingSelection === 'new' ? 'new' : 'saved',
    billingAddressId: billingSelection === 'new' ? null : billingSelection,
    saveBillingAddress: !billingSameAsShipping && billingSelection === 'new' ? saveBillingAddress : false,
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!user) return;

    if (!preInformationAccepted || !distanceSalesAccepted || !returnPolicyAccepted) {
      setError('Siparişi tamamlamak için gerekli sözleşme onaylarını işaretlemelisiniz.');
      return;
    }

    setError('');
    setLoading(true);

    try {
      const form = new FormData(e.currentTarget);
      const consents = {
        preInformationAccepted,
        distanceSalesAccepted,
        returnPolicyAccepted,
      };
      const orderPayload = buildIyzicoOrderRequest(
        items,
        totals,
        form,
        consents,
        appliedCoupon?.code,
        addressState,
      );
      const initiatePayload = buildIyzicoInitiateRequest(
        items,
        totals,
        form,
        consents,
        appliedCoupon?.code,
        addressState,
      );

      const response = await paymentService.initializeIyzico(initiatePayload);

      savePendingIyzicoCheckout(response.token, {
        conversationId: response.conversationId,
        order: orderPayload,
      });
      setPaymentToken(response.token);
      setConversationId(response.conversationId);

      if (response.paymentPageUrl) {
        if (isMockPaymentUrl(response.paymentPageUrl)) {
          setIsBackendMockPayment(true);
          setStep('payment');
          return;
        }

        window.location.href = response.paymentPageUrl;
        return;
      }

      if (response.checkoutFormContent) {
        setCheckoutFormContent(response.checkoutFormContent);
        setStep('payment');
        return;
      }

      if (env.useMock) {
        setStep('payment');
        return;
      }

      throw new Error('Ödeme formu alınamadı. Lütfen daha sonra tekrar deneyin.');
    } catch (err) {
      setError(getErrorMessage(err, 'Ödeme başlatılamadı.'));
    } finally {
      setLoading(false);
    }
  };

  const handleMockComplete = async () => {
    if (!user || !paymentToken || !conversationId) return;

    setCompletingMock(true);
    setError('');

    try {
      navigate(
        `/checkout/result?token=${encodeURIComponent(paymentToken)}&conversationId=${encodeURIComponent(conversationId)}&status=success`,
      );
    } catch (err) {
      setError(getErrorMessage(err, 'Test ödemesi tamamlanamadı.'));
    } finally {
      setCompletingMock(false);
    }
  };

  if (items.length === 0) {
    navigate('/cart');
    return null;
  }

  const showInlineShipping = shippingSelection === 'new' || savedAddresses.length === 0;
  const showInlineBilling = !billingSameAsShipping && (billingSelection === 'new' || savedAddresses.length === 0);

  return (
    <div className="container checkout-page">
      <h1>Ödeme</h1>

      {step === 'payment' && (
        <p className="checkout-step-label">
          <button type="button" className="checkout-back-link" onClick={() => setStep('details')}>
            <ArrowLeft size={16} aria-hidden />
            Bilgileri Düzenle
          </button>
        </p>
      )}

      {error && <div className="auth-error">{error}</div>}

      <div className="checkout-layout">
        {step === 'details' ? (
          <form className="checkout-form" onSubmit={handleSubmit}>
            <section>
              <h2>İletişim Bilgileri</h2>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="firstName">Ad</label>
                  <input id="firstName" name="firstName" defaultValue={firstName} required />
                </div>
                <div className="form-group">
                  <label htmlFor="lastName">Soyad</label>
                  <input id="lastName" name="lastName" defaultValue={lastName} required />
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="email">E-posta</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  defaultValue={user?.email ?? ''}
                  required
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="phone">Telefon</label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    placeholder="+905xxxxxxxxx"
                    defaultValue={defaultShipping?.phone ?? ''}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="identityNumber">TC Kimlik No</label>
                  <input
                    id="identityNumber"
                    name="identityNumber"
                    inputMode="numeric"
                    pattern="[0-9]{11}"
                    maxLength={11}
                    placeholder="11 haneli"
                    required
                  />
                  <span className="form-hint">Yabancı uyruklu alıcılar için 11111111111 kullanılabilir.</span>
                </div>
              </div>
            </section>

            <section>
              <h2>Teslimat Adresi</h2>
              {addressesLoading ? (
                <p>Kayıtlı adresler yükleniyor...</p>
              ) : savedAddresses.length > 0 ? (
                <AddressSelectList
                  name="shippingAddressSelection"
                  addresses={savedAddresses}
                  value={shippingSelection}
                  onChange={setShippingSelection}
                />
              ) : null}

              {showInlineShipping && (
                <>
                  <div className="form-group">
                    <label htmlFor="address">Adres</label>
                    <textarea id="address" name="address" rows={3} required={showInlineShipping} />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="city">Şehir</label>
                      <input id="city" name="city" required={showInlineShipping} />
                    </div>
                    <div className="form-group">
                      <label htmlFor="district">İlçe</label>
                      <input id="district" name="district" required={showInlineShipping} />
                    </div>
                  </div>
                  <div className="form-group">
                    <label htmlFor="zip">Posta Kodu</label>
                    <input id="zip" name="zip" required={showInlineShipping} />
                  </div>
                  <label className="address-checkbox">
                    <input
                      type="checkbox"
                      checked={saveShippingAddress}
                      onChange={(event) => setSaveShippingAddress(event.target.checked)}
                    />
                    Bu teslimat adresini kaydet
                  </label>
                </>
              )}
            </section>

            <section>
              <h2>Fatura Adresi</h2>
              <label className="address-checkbox">
                <input
                  type="checkbox"
                  checked={billingSameAsShipping}
                  onChange={(event) => setBillingSameAsShipping(event.target.checked)}
                />
                Fatura adresi teslimat ile aynı
              </label>

              {!billingSameAsShipping && (
                <>
                  {savedAddresses.length > 0 && (
                    <AddressSelectList
                      name="billingAddressSelection"
                      addresses={savedAddresses}
                      value={billingSelection}
                      onChange={setBillingSelection}
                      newOptionLabel="Yeni fatura adresi gir"
                    />
                  )}

                  {showInlineBilling && (
                    <>
                      <div className="form-group">
                        <label htmlFor="billingAddress">Fatura Adresi</label>
                        <textarea
                          id="billingAddress"
                          name="billingAddress"
                          rows={3}
                          required={showInlineBilling}
                        />
                      </div>
                      <div className="form-row">
                        <div className="form-group">
                          <label htmlFor="billingCity">Şehir</label>
                          <input id="billingCity" name="billingCity" required={showInlineBilling} />
                        </div>
                        <div className="form-group">
                          <label htmlFor="billingDistrict">İlçe</label>
                          <input id="billingDistrict" name="billingDistrict" required={showInlineBilling} />
                        </div>
                      </div>
                      <div className="form-group">
                        <label htmlFor="billingZip">Posta Kodu</label>
                        <input id="billingZip" name="billingZip" required={showInlineBilling} />
                      </div>
                      <label className="address-checkbox">
                        <input
                          type="checkbox"
                          checked={saveBillingAddress}
                          onChange={(event) => setSaveBillingAddress(event.target.checked)}
                        />
                        Bu fatura adresini kaydet
                      </label>
                    </>
                  )}
                </>
              )}
            </section>

            <section>
              <h2>Sözleşmeler ve Onaylar</h2>
              <PreInformationSummary
                items={items}
                subtotal={totals.subtotal}
                appliedCoupon={appliedCoupon}
              />

              <div className="consent-group">
                <ConsentCheckbox
                  id="preInformationAccepted"
                  checked={preInformationAccepted}
                  onChange={setPreInformationAccepted}
                  required
                >
                  Yukarıdaki ön bilgilendirme formunu okudum ve gerekli bilgilendirmeyi aldım.
                </ConsentCheckbox>

                <ConsentCheckbox
                  id="distanceSalesAccepted"
                  checked={distanceSalesAccepted}
                  onChange={setDistanceSalesAccepted}
                  required
                >
                  <LegalLink to="/yasal/mesafeli-satis-sozlesmesi">Mesafeli Satış Sözleşmesi</LegalLink>
                  &apos;ni okudum ve kabul ediyorum.
                </ConsentCheckbox>

                <ConsentCheckbox
                  id="returnPolicyAccepted"
                  checked={returnPolicyAccepted}
                  onChange={setReturnPolicyAccepted}
                  required
                >
                  <LegalLink to="/yasal/iptal-iade-kosullari">Cayma – İptal ve İade Koşulları</LegalLink>
                  &apos;nı okudum.
                </ConsentCheckbox>
              </div>
            </section>

            <section className="checkout-payment-info">
              <h2>Ödeme</h2>
              <p>
                Kart bilgileriniz güvenli iyzico ödeme altyapısı üzerinden işlenir.
                Bir sonraki adımda iyzico ödeme ekranına yönlendirileceksiniz.
              </p>
            </section>

            <button type="submit" className="btn btn-primary btn-lg btn-block" disabled={loading}>
              {loading
                ? 'iyzico hazırlanıyor...'
                : `${formatAmount(totals.total)} — iyzico ile Öde`}
            </button>
          </form>
        ) : (
          <div className="checkout-form checkout-payment-step">
            <section>
              <h2>iyzico ile Ödeme</h2>
              {checkoutFormContent ? (
                <IyzicoCheckoutForm checkoutFormContent={checkoutFormContent} />
              ) : env.useMock || isBackendMockPayment ? (
                <div className="iyzico-mock-panel">
                  <p>
                    <strong>Test modu:</strong> Backend mock ödeme aktif. Gerçek kart ekranı için
                    backend&apos;de <code>Iyzico:UseMock=false</code> ve sandbox API anahtarları
                    gerekir. Aşağıdaki butonla test ödemesini simüle edebilirsiniz.
                  </p>
                  <button
                    type="button"
                    className="btn btn-primary btn-lg btn-block"
                    disabled={completingMock || !paymentToken}
                    onClick={() => void handleMockComplete()}
                  >
                    {completingMock ? 'Tamamlanıyor...' : 'Test Ödemesini Tamamla'}
                  </button>
                </div>
              ) : (
                <p>Ödeme formu yüklenemedi. Lütfen sayfayı yenileyip tekrar deneyin.</p>
              )}
            </section>
          </div>
        )}

        <aside className="cart-summary">
          <h2>Sipariş Özeti</h2>

          <CouponInput subtotal={totals.subtotal} currency={STORE_CURRENCY} />

          {items.map(({ product, quantity }) => (
            <div key={product.id} className="checkout-item">
              <img src={getPrimaryImage(product)} alt={product.name} />
              <div>
                <p>{product.name}</p>
                <span>x{quantity}</span>
              </div>
              <span>{formatAmount(getUnitPrice(product) * quantity, product.currency)}</span>
            </div>
          ))}

          <div className="summary-row">
            <span>Ara Toplam</span>
            <span>{formatAmount(totals.subtotal)}</span>
          </div>
          {totals.discountAmount > 0 && (
            <div className="summary-row summary-discount">
              <span>İndirim{appliedCoupon ? ` (${appliedCoupon.code})` : ''}</span>
              <span>−{formatAmount(totals.discountAmount)}</span>
            </div>
          )}
          <div className="summary-row">
            <span>Kargo</span>
            <span>{totals.shipping === 0 ? 'Ücretsiz' : formatAmount(totals.shipping)}</span>
          </div>
          <div className="summary-row summary-total">
            <span>Toplam</span>
            <span>{formatAmount(totals.total)}</span>
          </div>

          <p className="checkout-iyzico-badge">
            Güvenli ödeme: <strong>iyzico</strong>
          </p>
        </aside>
      </div>
    </div>
  );
}
